import { useState, useRef, FormEvent, useEffect, useCallback } from 'react';
import { getSupabase } from '@/lib/supabase';
import {
  getMinTravelDate,
  getMinAppointmentDate,
  isTravelDateValid,
  isAppointmentDateValid,
  TRAVEL_DATE_HINT,
  TRAVEL_DATE_ERROR,
  APPOINTMENT_DATE_HINT,
  APPOINTMENT_DATE_ERROR,
} from '@/lib/dateRestrictions';

interface FormState {
  full_name: string;
  phone: string;
  email: string;
  nationality: string;
  country_of_travel: string;
  visa_type: string;
  purpose_of_travel: string;
  intended_travel_date: string;
  passport_number: string;
  appointment_date: string;
  appointment_time: string;
  additional_notes: string;
}

const INITIAL_FORM: FormState = {
  full_name: '',
  phone: '',
  email: '',
  nationality: '',
  country_of_travel: '',
  visa_type: '',
  purpose_of_travel: '',
  intended_travel_date: '',
  passport_number: '',
  appointment_date: '',
  appointment_time: '',
  additional_notes: '',
};

// Hardcoded default appointment times — ALWAYS available unless a
// confirmed/adjusted booking occupies that exact date+time in Supabase.
// Format: 24-hour HH:mm, displayed to client as friendly labels.
const DEFAULT_TIMES: string[] = [
  '08:00', '08:30', '09:00', '09:30',
  '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30',
  '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30',
];

function toDisplayLabel(time: string): string {
  const [hRaw, m] = time.split(':');
  const h = parseInt(hRaw, 10);
  const period = h >= 12 ? 'PM' : 'AM';
  const displayH = h > 12 ? h - 12 : (h === 0 ? 12 : h);
  return `${displayH}:${m} ${period}`;
}

// Only these appointment statuses remove a time from availability
const BLOCKING_STATUSES = ['confirmed', 'time_adjusted', 'date_adjusted', 'date_time_adjusted'];

export default function ApplyNow() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ referenceNumber: string } | null>(null);

  // Time slot state
  const [availableTimes, setAvailableTimes] = useState<string[]>([]);
  const [timesLoading, setTimesLoading] = useState(false);
  const [timesError, setTimesError] = useState(false);

  const passportCopyRef = useRef<HTMLInputElement>(null);
  const passportPhotoRef = useRef<HTMLInputElement>(null);
  const bankStatementRef = useRef<HTMLInputElement>(null);
  const invitationLetterRef = useRef<HTMLInputElement>(null);
  const otherDocsRef = useRef<HTMLInputElement>(null);

  const countries = ['UAE','Qatar','China','South Africa','Namibia','DRC','Congo','Egypt','Algeria','Zimbabwe','Malawi','Lebanon','Sri Lanka','Indonesia','Tanzania','Zambia','Thailand','Saudi Arabia','Georgia','Bahrain','France','Guinea-Bissau','Madagascar','Uganda','Turkey','India','Senegal','Cyprus','Nigeria','Other'];
  const visaTypes = ['Tourist','Business','Student','Work','Transit','Medical','Family Visit','Diplomatic','Other'];
  const purposes = ['Tourism','Business Meeting','Conference','Study','Medical Treatment','Family Visit','Employment','Transit','Other'];

  const update = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const fetchAvailableTimes = useCallback(async (selectedDate: string) => {
    if (!selectedDate) return;

    setTimesLoading(true);
    setTimesError(false);

    try {
      const supabase = getSupabase();
      const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
      const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

      const fnUrl = `${supabaseUrl}/functions/v1/manage-appointments?action=blocked-times&date=${encodeURIComponent(selectedDate)}`;

      const response = await fetch(fnUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'apikey': anonKey,
          'Authorization': `Bearer ${anonKey}`,
        },
      });

      if (!response.ok) {
        console.error('Edge function returned non-ok status:', response.status);
        setTimesError(true);
        setAvailableTimes([...DEFAULT_TIMES]);
        return;
      }

      const data = await response.json();

      if (!data || !data.success) {
        console.error('Edge function returned unsuccessful response:', data);
        setTimesError(true);
        setAvailableTimes([...DEFAULT_TIMES]);
        return;
      }

      // data.blocked_times includes both confirmed/adjusted bookings AND active temp reservations
      const blockedSet = new Set<string>(data.blocked_times || []);
      const filtered = DEFAULT_TIMES.filter((t) => !blockedSet.has(t));

      setAvailableTimes(filtered);

      // If currently selected time is now blocked, clear it
      if (form.appointment_time && blockedSet.has(form.appointment_time)) {
        setForm((prev) => ({ ...prev, appointment_time: '' }));
      }
    } catch (err: unknown) {
      console.error('Failed to fetch blocked times:', err);
      setTimesError(true);
      setAvailableTimes([...DEFAULT_TIMES]);
    } finally {
      setTimesLoading(false);
    }
  }, [form.appointment_time]);

  // Fetch blocked times when the appointment date changes
  useEffect(() => {
    if (!form.appointment_date) {
      setAvailableTimes([]);
      setTimesError(false);
      return;
    }

    fetchAvailableTimes(form.appointment_date);
  }, [form.appointment_date]); // eslint-disable-line react-hooks/exhaustive-deps

  const canProceedToStep3 = step === 2 && form.country_of_travel && form.visa_type && form.purpose_of_travel && form.intended_travel_date && form.appointment_date && form.appointment_time;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Rule 1 — Intended Travel Date must be at least 15 days from today.
    if (!isTravelDateValid(form.intended_travel_date)) {
      setError(TRAVEL_DATE_ERROR);
      setStep(2);
      return;
    }

    // Rule 2 — Appointment Date must be at least 2 days from today.
    if (!isAppointmentDateValid(form.appointment_date)) {
      setError(APPOINTMENT_DATE_ERROR);
      setStep(2);
      return;
    }

    setSubmitting(true);

    const formData = new FormData();

    formData.append('full_name', form.full_name.trim());
    formData.append('phone', form.phone.trim());
    formData.append('email', form.email.trim());
    formData.append('nationality', form.nationality.trim());
    formData.append('country_of_travel', form.country_of_travel);
    formData.append('visa_type', form.visa_type);
    formData.append('purpose_of_travel', form.purpose_of_travel);
    formData.append('intended_travel_date', form.intended_travel_date);
    formData.append('passport_number', form.passport_number.trim());
    formData.append('appointment_date', form.appointment_date);
    formData.append('appointment_time', form.appointment_time);
    formData.append('additional_notes', form.additional_notes.trim());
    formData.append('application_type', 'office_appointment');

    const fileRefs = [
      { ref: passportCopyRef, name: 'passport_copy' },
      { ref: passportPhotoRef, name: 'passport_photo' },
      { ref: bankStatementRef, name: 'bank_statement' },
      { ref: invitationLetterRef, name: 'invitation_letter' },
      { ref: otherDocsRef, name: 'other_documents' },
    ];
    for (const { ref, name } of fileRefs) {
      const file = ref.current?.files?.[0];
      if (file) {
        formData.append(name, file);
      }
    }

    try {
      const response = await fetch('https://piwmqcjmzbmpwpnogfht.supabase.co/functions/v1/submit-application', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setResult({ referenceNumber: data.reference_number });
      } else {
        const errData = await response.json().catch(() => ({ error: 'Something went wrong.' }));
        setError(errData.error || 'Something went wrong. Please try again or contact us directly.');
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Connection failed: ${message}. Please try again or contact us directly.`);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <div>
        <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20dark%20navy%20blue%20background%20with%20soft%20orange%20glow%20and%20success%20check%20mark%20visual%2C%20premium%20corporate%20style%2C%20celebration%20theme%2C%20clean%20modern%20design&width=1920&height=600&seq=apply-now-success&orientation=landscape"
            alt="Application submitted successfully"
            className="absolute inset-0 w-full h-full object-cover object-top"
            title="Application Received - Premium Visa Solutions"
          />
          <div className="absolute inset-0 bg-primary-950/70"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Application Received!</h1>
          </div>
        </section>
        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full max-w-[700px] mx-auto px-4 md:px-8 text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center bg-accent-500 text-background-50 rounded-full mb-6">
              <i className="ri-check-line text-3xl"></i>
            </div>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-4">
              Thank You. Your Office Appointment Request Has Been Received.
            </h2>
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-6 mb-6 inline-block">
              <p className="text-sm text-foreground-600 mb-2">Your Premium Visa Solutions reference number is:</p>
              <p className="font-heading text-2xl md:text-3xl font-bold text-primary-700">{result.referenceNumber}</p>
            </div>
            <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 mb-6 text-left max-w-[420px] mx-auto">
              <p className="text-xs font-semibold text-foreground-700 mb-2 flex items-center gap-1.5">
                <i className="ri-building-line text-primary-600"></i> Office Address for Your Visit
              </p>
              <p className="text-sm text-foreground-800">Premium Visa Solutions</p>
              <p className="text-sm text-foreground-600">Lusaka, Zambia</p>
            </div>
            <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-[500px] mx-auto">
              Your office appointment request has been received and is now pending review. Please visit our office with your original documents at the confirmed time.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <a href="/track-application" className="w-full sm:w-auto px-8 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Track Your Application</a>
              <a href="/" className="w-full sm:w-auto px-8 py-3 bg-secondary-100 text-foreground-700 font-semibold rounded-md hover:bg-secondary-200 transition-colors text-sm whitespace-nowrap cursor-pointer">Back to Home</a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="relative h-[280px] md:h-[350px] flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Professional%20modern%20office%20reception%20area%20with%20warm%20lighting%2C%20navy%20blue%20and%20orange%20accent%20colors%2C%20welcoming%20corporate%20environment%2C%20clean%20minimalist%20design%2C%20visa%20consultancy%20office%20setting%20with%20comfortable%20seating%20area&width=1920&height=600&seq=apply-now-office&orientation=landscape"
          alt="Apply Now - visit our office"
          className="absolute inset-0 w-full h-full object-cover object-top"
          title="Apply Now - Premium Visa Solutions Office"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 to-primary-950/65"></div>
        <div className="relative z-10 text-center px-4">
          <div className="inline-flex items-center gap-2 bg-accent-500/20 border border-accent-400/40 rounded-full px-4 py-1.5 mb-4">
            <i className="ri-building-line text-accent-300 text-sm"></i>
            <span className="text-accent-200 text-xs font-semibold">Office Appointment</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-3">Apply Now – Visit Our Office</h1>
          <p className="text-sm md:text-base text-background-50/80 max-w-[600px] mx-auto">
            Submit your visa application and schedule an in-person appointment at the Premium Visa Solutions office
          </p>
        </div>
      </section>

      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[900px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-center gap-2 mb-10">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-sm font-semibold transition-colors ${s <= step ? 'bg-accent-500 text-background-50' : 'bg-secondary-200 text-foreground-500'}`}>{s}</div>
                {s < 3 && <div className={`w-8 md:w-12 h-0.5 ${s < step ? 'bg-accent-500' : 'bg-secondary-200'}`}></div>}
              </div>
            ))}
          </div>
          <div className="flex justify-center gap-6 md:gap-12 text-xs md:text-sm font-medium text-foreground-600 mb-10">
            <span className={step >= 1 ? 'text-accent-600' : ''}>Personal Details</span>
            <span className={step >= 2 ? 'text-accent-600' : ''}>Travel & Office Appointment</span>
            <span className={step >= 3 ? 'text-accent-600' : ''}>Documents & Submit</span>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-md p-3 mb-6">{error}</div>
          )}

          <form onSubmit={handleSubmit} data-readdy-form="">
            {step === 1 && (
              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-4">Personal Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="fullName" className="block text-sm font-medium text-foreground-700 mb-1.5">Full Name <span className="text-accent-600">*</span></label>
                    <input type="text" id="fullName" name="full_name" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="Enter your full name" value={form.full_name} onChange={(e) => update('full_name', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-foreground-700 mb-1.5">Phone Number <span className="text-accent-600">*</span></label>
                    <input type="tel" id="phone" name="phone" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="+260 XXX XXX XXX" value={form.phone} onChange={(e) => update('phone', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">Email Address <span className="text-accent-600">*</span></label>
                    <input type="email" id="email" name="email" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="your@email.com" value={form.email} onChange={(e) => update('email', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="nationality" className="block text-sm font-medium text-foreground-700 mb-1.5">Nationality <span className="text-accent-600">*</span></label>
                    <input type="text" id="nationality" name="nationality" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="e.g. Zambian" value={form.nationality} onChange={(e) => update('nationality', e.target.value)} />
                  </div>
                </div>
                <div className="flex justify-end pt-4">
                  <button type="button" onClick={() => setStep(2)} className="px-10 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 transition-colors text-sm whitespace-nowrap cursor-pointer">Next: Travel & Appointment</button>
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-4">Travel & Office Appointment Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="countryTravel" className="block text-sm font-medium text-foreground-700 mb-1.5">Country of Travel <span className="text-accent-600">*</span></label>
                    <select id="countryTravel" name="country_of_travel" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.country_of_travel} onChange={(e) => update('country_of_travel', e.target.value)}>
                      <option value="">Select country</option>
                      {countries.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="visaType" className="block text-sm font-medium text-foreground-700 mb-1.5">Visa Type <span className="text-accent-600">*</span></label>
                    <select id="visaType" name="visa_type" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.visa_type} onChange={(e) => update('visa_type', e.target.value)}>
                      <option value="">Select visa type</option>
                      {visaTypes.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="purpose" className="block text-sm font-medium text-foreground-700 mb-1.5">Purpose of Travel <span className="text-accent-600">*</span></label>
                    <select id="purpose" name="purpose_of_travel" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.purpose_of_travel} onChange={(e) => update('purpose_of_travel', e.target.value)}>
                      <option value="">Select purpose</option>
                      {purposes.map((p) => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="travelDate" className="block text-sm font-medium text-foreground-700 mb-1.5">Intended Travel Date <span className="text-accent-600">*</span></label>
                    <input type="date" id="travelDate" name="intended_travel_date" required min={getMinTravelDate()} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.intended_travel_date} onChange={(e) => update('intended_travel_date', e.target.value)} />
                    {form.intended_travel_date && !isTravelDateValid(form.intended_travel_date) ? (
                      <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line"></i>{TRAVEL_DATE_ERROR}</p>
                    ) : (
                      <p className="text-xs text-foreground-500 mt-1.5">{TRAVEL_DATE_HINT}</p>
                    )}
                  </div>
                  <div>
                    <label htmlFor="passportNumber" className="block text-sm font-medium text-foreground-700 mb-1.5">Passport Number</label>
                    <input type="text" id="passportNumber" name="passport_number" className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors" placeholder="Enter your passport number" value={form.passport_number} onChange={(e) => update('passport_number', e.target.value)} />
                  </div>
                  <div>
                    <label htmlFor="appointmentDate" className="block text-sm font-medium text-foreground-700 mb-1.5">Preferred Office Appointment Date <span className="text-accent-600">*</span></label>
                    <input type="date" id="appointmentDate" name="appointment_date" required min={getMinAppointmentDate()} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.appointment_date} onChange={(e) => update('appointment_date', e.target.value)} />
                    {form.appointment_date && !isAppointmentDateValid(form.appointment_date) ? (
                      <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1"><i className="ri-error-warning-line"></i>{APPOINTMENT_DATE_ERROR}</p>
                    ) : (
                      <p className="text-xs text-foreground-500 mt-1.5">{APPOINTMENT_DATE_HINT}</p>
                    )}
                  </div>
                </div>
                <div>
                  <label htmlFor="appointmentTime" className="block text-sm font-medium text-foreground-700 mb-1.5">Preferred Office Appointment Time <span className="text-accent-600">*</span></label>

                  {!form.appointment_date ? (
                    <div className="bg-secondary-100 border border-secondary-200 rounded-md p-4 text-center">
                      <i className="ri-calendar-line text-foreground-400 text-lg mb-1 block"></i>
                      <p className="text-sm text-foreground-500">Please select an appointment date first.</p>
                    </div>
                  ) : timesLoading ? (
                    <div className="flex items-center gap-2 text-sm text-foreground-500 py-2">
                      <i className="ri-loader-4-line animate-spin"></i> Loading available times...
                    </div>
                  ) : timesError ? (
                    <div className="mb-3">
                      <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-3 text-center">
                        <i className="ri-error-warning-line text-amber-500 text-lg mb-1 block"></i>
                        <p className="text-sm text-amber-700 font-medium">We could not verify existing bookings.</p>
                        <p className="text-xs text-amber-600 mt-1">You may still choose a preferred time. Final availability will be confirmed by the administrator.</p>
                      </div>
                      <select id="appointmentTime" name="appointment_time" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.appointment_time} onChange={(e) => update('appointment_time', e.target.value)}>
                        <option value="">Select time slot</option>
                        {availableTimes.map((t) => (
                          <option key={t} value={t}>{toDisplayLabel(t)}</option>
                        ))}
                      </select>
                    </div>
                  ) : availableTimes.length === 0 ? (
                    <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-center">
                      <i className="ri-time-line text-amber-500 text-lg mb-1 block"></i>
                      <p className="text-sm text-amber-700 font-medium">No appointment times are available for the selected date.</p>
                      <p className="text-xs text-amber-600 mt-1">All appointment slots have been confirmed for this date. Please try another date.</p>
                    </div>
                  ) : (
                    <select id="appointmentTime" name="appointment_time" required className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors cursor-pointer" value={form.appointment_time} onChange={(e) => update('appointment_time', e.target.value)}>
                      <option value="">Select time slot</option>
                      {availableTimes.map((t) => (
                        <option key={t} value={t}>{toDisplayLabel(t)}</option>
                      ))}
                    </select>
                  )}
                </div>
                <div className="flex justify-between pt-4">
                  <button type="button" onClick={() => setStep(1)} className="px-8 py-3 bg-secondary-100 text-foreground-700 font-medium rounded-md hover:bg-secondary-200 transition-colors text-sm whitespace-nowrap cursor-pointer">Back</button>
                  <button type="button" onClick={() => setStep(3)} disabled={!canProceedToStep3} className="px-10 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors text-sm whitespace-nowrap cursor-pointer">Next: Documents & Submit</button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-4">
                <h3 className="font-heading text-xl font-semibold text-foreground-900 mb-4">Documents & Submit</h3>
                <div className="bg-secondary-100 border border-secondary-200 rounded-lg p-4 mb-4">
                  <p className="text-sm font-medium text-foreground-800 mb-2">
                    <i className="ri-information-line mr-1"></i> Required Documents Checklist for Office Visit
                  </p>
                  <ul className="space-y-1.5 text-sm text-foreground-600">
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i> Valid passport (original + copy)</li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i> Passport-sized photographs</li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i> Bank statements (last 3 months)</li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i> Invitation letter or hotel booking</li>
                    <li className="flex items-start gap-2"><i className="ri-checkbox-circle-line text-accent-600 mt-0.5"></i> Any other supporting documents</li>
                  </ul>
                </div>
                <p className="text-sm text-foreground-600 mb-2">You can upload copies now or bring them to your appointment. Accepted formats: PDF, JPG, PNG (max 5MB each).</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { id: 'passportCopy', name: 'passport_copy', label: 'Passport Copy', ref: passportCopyRef },
                    { id: 'passportPhoto', name: 'passport_photo', label: 'Passport Photo', ref: passportPhotoRef },
                    { id: 'bankStatement', name: 'bank_statement', label: 'Bank Statement', ref: bankStatementRef },
                    { id: 'invitationLetter', name: 'invitation_letter', label: 'Invitation Letter / Hotel Booking', ref: invitationLetterRef },
                    { id: 'otherDocs', name: 'other_documents', label: 'Other Supporting Documents', ref: otherDocsRef },
                  ].map((doc) => (
                    <div key={doc.id}>
                      <label htmlFor={`now-${doc.id}`} className="block text-sm font-medium text-foreground-700 mb-1.5">{doc.label}</label>
                      <input type="file" id={`now-${doc.id}`} name={doc.name} accept=".pdf,.jpg,.jpeg,.png" ref={doc.ref} className="w-full text-sm text-foreground-600 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 file:cursor-pointer" />
                    </div>
                  ))}
                </div>
                <div>
                  <label htmlFor="additionalNotes" className="block text-sm font-medium text-foreground-700 mb-1.5">Additional Notes</label>
                  <textarea id="additionalNotes" name="additional_notes" maxLength={500} rows={4} className="w-full px-4 py-2.5 text-sm border border-secondary-300 rounded-md bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none" placeholder="Any additional information you would like us to know..." value={form.additional_notes} onChange={(e) => update('additional_notes', e.target.value)}></textarea>
                  <p className="text-xs text-foreground-500 mt-1">Maximum 500 characters</p>
                </div>
                <div className="flex justify-between pt-4">
                  <button type="button" onClick={() => setStep(2)} className="px-8 py-3 bg-secondary-100 text-foreground-700 font-medium rounded-md hover:bg-secondary-200 transition-colors text-sm whitespace-nowrap cursor-pointer">Back</button>
                  <button type="submit" disabled={submitting} className="px-10 py-3 bg-accent-500 text-background-50 font-semibold rounded-md hover:bg-accent-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 text-sm whitespace-nowrap cursor-pointer">
                    {submitting ? (<span className="flex items-center gap-2"><i className="ri-loader-4-line animate-spin"></i>Submitting...</span>) : 'Submit Application'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
}